import React, { useState, useRef, useEffect } from "react";
import { postApi } from "../../services/api/postApi";
import {
  contentTuningApi,
  userManagementApi,
} from "../../services/api/contentTuningApi";
import {
  FaEllipsisH,
  FaEdit,
  FaTrash,
  FaEyeSlash,
  FaFlag,
  FaThumbsDown,
  FaUserSlash,
  FaMinusCircle,
} from "react-icons/fa";
import EditPostModal from "./EditPostModal";
import ReportModal from "./ReportModal";
import ConfirmModal from "./ConfirmModal";
import "./PostMenu.css";

const PostMenu = ({ post, onDelete, onUpdate }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHideModal, setShowHideModal] = useState(false);
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [showNotInterestedModal, setShowNotInterestedModal] = useState(false);
  const [showReduceTypeModal, setShowReduceTypeModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const menuRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));

  // Get current user ID (backend uses 'id', not '_id')
  const currentUserId = user?.id || user?._id || user?.userId;

  // Check if current user is the post author
  const isAuthor = String(post.author?._id) === String(currentUserId);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setShowMenu(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await postApi.deletePost(post._id, false); // soft delete
      onDelete(post._id);
      setShowDeleteModal(false);
    } catch (error) {
      alert("Failed to delete post");
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = () => {
    setShowEditModal(true);
    setShowMenu(false);
  };

  const handleHideClick = () => {
    setShowHideModal(true);
    setShowMenu(false);
  };

  const handleHideConfirm = () => {
    onDelete(post._id);
    setShowHideModal(false);
  };

  const handleReport = () => {
    setShowReportModal(true);
    setShowMenu(false);
  };

  const handleNotInterested = () => {
    setShowNotInterestedModal(true);
    setShowMenu(false);
  };

  const handleNotInterestedConfirm = async () => {
    setActionLoading(true);
    try {
      await contentTuningApi.markNotInterested(post._id);
      if (onDelete) onDelete(post._id);
      setShowNotInterestedModal(false);
    } catch (error) {
      // Silent fail
    } finally {
      setActionLoading(false);
    }
  };

  const handleMuteUser = () => {
    setShowMuteModal(true);
    setShowMenu(false);
  };

  const handleMuteUserConfirm = async () => {
    setActionLoading(true);
    try {
      await userManagementApi.muteUser(post.author._id);
      if (onDelete) onDelete(post._id);
      setShowMuteModal(false);
    } catch (error) {
      // Silent fail
    } finally {
      setActionLoading(false);
    }
  };

  const handleReducePostType = () => {
    setShowReduceTypeModal(true);
    setShowMenu(false);
  };

  const handleReducePostTypeConfirm = async () => {
    setActionLoading(true);
    try {
      await contentTuningApi.reducePostType(post.postType);
      setShowReduceTypeModal(false);
    } catch (error) {
      // Silent fail
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSuccess = (updatedPost) => {
    if (onUpdate) {
      onUpdate(post._id, updatedPost);
    }
  };

  return (
    <div className="post-menu" ref={menuRef}>
      <button
        className="menu-trigger"
        onClick={() => setShowMenu(!showMenu)}
        aria-label="Post options"
      >
        <FaEllipsisH />
      </button>

      {showMenu && (
        <div className="menu-dropdown">
          {isAuthor ? (
            <>
              <button className="menu-item" onClick={handleEdit}>
                <FaEdit /> Edit Post
              </button>
              <button className="menu-item danger" onClick={handleDeleteClick}>
                <FaTrash /> Delete Post
              </button>
            </>
          ) : (
            <>
              <button className="menu-item" onClick={handleNotInterested}>
                <FaThumbsDown /> Not Interested
              </button>
              <button className="menu-item" onClick={handleHideClick}>
                <FaEyeSlash /> Hide Post
              </button>
              <button className="menu-item" onClick={handleReducePostType}>
                <FaMinusCircle /> Show Less "{post.postType}"
              </button>
              <button className="menu-item" onClick={handleMuteUser}>
                <FaUserSlash /> Mute {post.author?.name || "User"}
              </button>
              <div className="menu-divider" />
              <button className="menu-item danger" onClick={handleReport}>
                <FaFlag /> Report Post
              </button>
            </>
          )}
        </div>
      )}

      {/* Edit Post Modal */}
      {showEditModal && (
        <EditPostModal
          post={post}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType="post"
          contentId={post._id}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Post?"
        message="Are you sure you want to delete this post? This action cannot be undone and the post will be removed from your profile."
        confirmText="Delete Post"
        cancelText="Cancel"
        type="danger"
        loading={deleting}
      />

      {/* Hide Confirmation Modal */}
      <ConfirmModal
        isOpen={showHideModal}
        onClose={() => setShowHideModal(false)}
        onConfirm={handleHideConfirm}
        title="Hide Post?"
        message="This post will be hidden from your feed. You can still find it in your hidden posts."
        confirmText="Hide Post"
        cancelText="Cancel"
        type="warning"
      />

      {/* Not Interested Modal */}
      <ConfirmModal
        isOpen={showNotInterestedModal}
        onClose={() => setShowNotInterestedModal(false)}
        onConfirm={handleNotInterestedConfirm}
        title="Not Interested?"
        message="We'll use this to improve your feed and show you less content like this."
        confirmText="Not Interested"
        cancelText="Cancel"
        type="info"
        loading={actionLoading}
      />

      {/* Mute User Modal */}
      <ConfirmModal
        isOpen={showMuteModal}
        onClose={() => setShowMuteModal(false)}
        onConfirm={handleMuteUserConfirm}
        title={`Mute ${post.author?.name || "User"}?`}
        message={`You won't see posts from ${
          post.author?.name || "this user"
        } in your feed anymore. You can unmute them anytime from settings.`}
        confirmText="Mute"
        cancelText="Cancel"
        type="warning"
        loading={actionLoading}
      />

      {/* Reduce Post Type Modal */}
      <ConfirmModal
        isOpen={showReduceTypeModal}
        onClose={() => setShowReduceTypeModal(false)}
        onConfirm={handleReducePostTypeConfirm}
        title={`Show Less "${post.postType}" Posts?`}
        message="This will reduce how often you see this type of content in your feed."
        confirmText="Show Less"
        cancelText="Cancel"
        type="info"
        loading={actionLoading}
      />
    </div>
  );
};

export default PostMenu;
